import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TeaserAssetStoreService } from '@app/shared/components/teaser/services/teaser-asset-store.service';
import { videoFileTypes } from '@app/shared/constants';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { DashboardModuleNames } from '@app/shared/enums';
import { TeaserAsset } from '@app/shared/models/teaser-asset';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { TeaserService } from '@app/shared/services/api/teaser.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { VirusScannerService } from '@app/shared/services/virus-scanner.service';
import { cssVariables } from '@env/css-vars';
import { TranslateService } from '@ngx-translate/core';
import { assign, delay, includes, isNil, map as _map, replace, every } from 'lodash';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ThumbnailsComponent } from './thumbnails/thumbnails.component';
@Component({
  selector: 'app-teaser-editor',
  templateUrl: './teaser-editor.component.html',
  styleUrls: ['./teaser-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TeaserEditorComponent implements OnInit, OnDestroy {
  protected _unsubscribe$: Subject<void> = new Subject<void>();
  private isImageSizeSupported$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  maxAssetCount: number = 0;

  assets: TeaserAsset[] = [];

  selectedAsset: TeaserAsset = {} as TeaserAsset;

  htmlContent = '';
  titleContent = '';
  btnTextContent = '';

  private initialTitleContent: string = '';
  private initialHtmlContent: string = '';
  private initialBtnTextContent: string = '';

  private hasUnsavedChanges: boolean = false;

  hasUnsupportedFile = false;
  previewURL: string = '';
  maxLength = 1000;
  maxLengthReached = false;
  maxLinesReached = false;
  editor: any;

  teaserComponent = '';

  _baseUrl = '';

  @ViewChild('autosize', ) autosize: CdkTextareaAutosize | undefined;
  @ViewChild('thumbnails', ) thumbnails: ThumbnailsComponent | undefined;

  constructor(
    public translate: TranslateService,
    public teaserService: TeaserService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _ngZone: NgZone,
    private _overlayLoaderService: OverlayLoaderService,
    private teaserAssetStore: TeaserAssetStoreService,
    private matDialog: MatDialog,
    private scannerService: VirusScannerService,
    private authService: AuthService,
    private azureBlobStorageService: AzureBlobStorageService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  triggerResize() {
    this._ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize?.resizeToFitContent(true));
  }

  ngOnInit() {
    this.teaserComponent = this._route.snapshot.paramMap.get('component') as string;
    this.teaserService.setTeaserComponent(this.teaserComponent);
    this._baseUrl = this.teaserService.getTeaserAssetStorageUrl();

    if (this.hasTeaserEditorPermissions()) {
      this.userMunicipalitiesService.currentMunicipality$
        .pipe(
          tap(() => this.teaserAssetStore.assets$.subscribe((assets) => (this.assets = assets))),
          tap(() => this.teaserAssetStore.selectedAsset$.subscribe((asset) => (this.selectedAsset = asset))),
          tap(() => {
            this.maxAssetCount = this.teaserAssetStore.maxAssetCount;
          }),
          tap(() =>
            this.teaserService.getTeaserAssetsFromCache().subscribe((config) => {
              this.titleContent = config[0];
              this.htmlContent = config[1];
              this.loadAssets(config[2]);
              this.btnTextContent = config[3];
              this.validateEditor(this.htmlContent);
              this.setInitialTextValue();
            })
          )
        )
        .subscribe({
          complete: () => (this._overlayLoaderService.isLoading = false),
        });
    } else {
      this.goToTeaserPage();
    }
  }

  private setInitialTextValue(): void {
    this.initialBtnTextContent = this.btnTextContent;
    this.initialHtmlContent = this.htmlContent;
    this.initialTitleContent = this.titleContent;
  }

  private hasTeaserEditorPermissions = () =>
    (this.authService.permissions.SENSOR_TEASER_EDITOR && this.teaserService.getTeaserComponent() === DashboardModuleNames.sensordaten.toLowerCase()) ||
    (this.authService.permissions.ROOM_MONITORING_EDITOR && this.teaserService.getTeaserComponent() === DashboardModuleNames.roommonitor.toLowerCase());

  async loadAssets(config: string) {
    const assets = JSON.parse(config).assets;
    const safeUrls: any[] = [];
    for (const asset of assets) {
      const src = asset.asset ? await this.azureBlobStorageService.getBlobImageSafeUrl(`${this._baseUrl}/${asset.asset}`).toPromise() : null;
      safeUrls.push(src);
    }
    this.teaserAssetStore.init(_map(assets, (a: any, i: number) => assign(a, { src: safeUrls[i] })));
    this._overlayLoaderService.isLoading = false;
    this._overlayLoaderService.isGlobalLoader = true;
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  redirectToSensor() {
    if (this.hasUnsavedChanges) {
      this.openExitConfirmationDialog();
    } else {
      this.goToTeaserPage();
    }
  }

  onFilesChanged(files: File[]) {
    this._overlayLoaderService.isLoading = true;
    this.isImageSizeSupported(files);
    setTimeout(() => {
      this.hasUnsupportedFile = this.hasUnsupportedFiles(files) || this.isImageSizeSupported$.value === false;
      if (this.hasUnsupportedFile) {
        this._overlayLoaderService.isLoading = false;
        return;
      }

      this.teaserService.uploadAsset(files[0]).subscribe({
        next: (response) => {
          if (response.fileName) {
            this.azureBlobStorageService.getBlobImageSafeUrl(`${this._baseUrl}/${response.fileName}`).subscribe((src) => {
              const uploaded = {
                index: this.selectedAsset.index,
                type: files[0].type,
                src,
                asset: response.fileName,
              };

              this.teaserAssetStore.replace(this.selectedAsset, uploaded);
              this.teaserAssetStore.select(uploaded);
              this._overlayLoaderService.isLoading = false;
            });
          }
        },
        error: (err) => {
          this.scannerService.errorChecker(err);
          this.selectedAsset.src = null;
          this._overlayLoaderService.isLoading = false;
        },
      });
    }, 2000);
  }

  onThumbnailSelect(asset: any) {
    this.teaserAssetStore.select(asset);
  }

  onThumbnailAdd() {
    const newAsset = this.teaserAssetStore.add();
    this.teaserAssetStore.select(newAsset);
  }

  onThumbnailDelete(asset: any) {
    this.teaserAssetStore.remove(asset);
  }

  public onChanges(): void {
    this.hasUnsavedChanges =
      this.initialBtnTextContent !== this.btnTextContent || this.initialHtmlContent !== this.htmlContent || this.initialTitleContent !== this.titleContent;
  }

  cancel() {
    this.openExitConfirmationDialog();
  }

  save() {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: this.translate.instant('teaser.confirmationDialog.updateConfirmationMessage'),
          descriptionFaClass: 'fa-question-circle fa-2x',
          confirmText: this.translate.instant('teaser.confirmationDialog.update'),
          declineText: this.translate.instant('teaser.confirmationDialog.cancel'),
          hideClose: true,
        },
        panelClass: ['-md', 'sensor-onboarding-confirmation'],
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this._overlayLoaderService.isLoading = true;
          this.setInitialTextValue();
          this.hasUnsavedChanges = false;
          forkJoin([
            this.teaserService.saveHeader(this.titleContent),
            this.teaserService.saveDetail(this.formatHtmlContent(this.htmlContent)),
            this.teaserService.saveEmailBtnText(this.btnTextContent),
            this.teaserService.saveAssets(this.assets),
          ]).subscribe(() => {
            this._overlayLoaderService.isLoading = false;
            const successDialog = this.matDialog.open(NotificationDialogComponent, {
              data: {
                descriptionText: this.translate.instant('teaser.confirmationDialog.saveSuccessMessage'),
                descriptionFaClass: 'fa-check-circle fa-2x',
                descriptionFaColor: cssVariables['--success-color'],
                hideClose: true,
              },
              panelClass: ['-md', 'sensor-onboarding-confirmation'],
            });
            delay(() => {
              successDialog.close();
            }, 3000);
            this.teaserService.clearTeaserAssetsCache();
            this.teaserService.cleanupAssets().subscribe();
            this.goToTeaserPage();
          });
        }
      });
  }

  objChanged($event: Event) {
    if ($event) {
      this.validateEditor($event);
    }
  }

  private openExitConfirmationDialog() {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: this.translate.instant('teaser.confirmationDialog.unsavedConfirmationText'),
          descriptionFaClass: 'fa-exclamation-triangle fa-4x',
          descriptionFaColor: '#E91313',
          confirmText: this.translate.instant('teaser.confirmationDialog.confirmText'),
          declineText: this.translate.instant('teaser.confirmationDialog.declineText'),
          hideClose: true,
        },
        panelClass: ['-md', 'sensor-onboarding-confirmation'],
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.teaserService.cleanupAssets().subscribe();
          this.goToTeaserPage();
        }
      });
  }

  private goToTeaserPage = () => {
    this._router.navigate(['../'], { relativeTo: this._route });
  };

  hasUnsupportedFiles(files: File[]): boolean {
    const supportedFileTypes = ['png', 'jpg', 'jpeg', 'mp4', 'wmv', 'x-ms-wmv'];

    return every(files, (file: File) => !includes(supportedFileTypes, file.type.split('/')[1].toLowerCase()) || file.size / 1024 / 1024 > 100);
  }

  isVideoType(file: File) {
    if (!isNil(file.type)) {
      return includes(videoFileTypes, file.type.split('/')[1].toLowerCase());
    } else {
      return false;
    }
  }

  onPreviousClick() {
    this.thumbnails?.onPreviousClick();
  }

  onNextClick() {
    this.thumbnails?.onNextClick();
  }

  isImageSizeSupported(files: File[]): void {
    let isImageSupported = false;
    const supportedImageFileTypes = ['png', 'jpg', 'jpeg'];

    every(files, (file) => {
      if (!isNil(file)) {
        if (!includes(supportedImageFileTypes, file.type.split('/')[1].toLowerCase())) {
          this.isImageSizeSupported$.next(true);
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e: ProgressEvent<FileReader>) => {
          const image = new Image();
          image.src = e.target?.result as string;
          image.onload = () => {
            const width = image.width;
            const height = image.height;

            window.URL.revokeObjectURL(image.src);

            isImageSupported = width >= 800 && width <= 1920 && height >= 400 && height <= 1080;

            this.isImageSizeSupported$.next(isImageSupported);
          };
        };
      } else {
        this.isImageSizeSupported$.next(false);
      }
    });
  }

  validateEditor($event: any) {
    if (!isNil($event.textValue) && !isNil($event.htmlValue)) {
      const visualText = $event.textValue;

      if (visualText.length > this.maxLength) {
        this.editor.deleteText(this.maxLength, this.editor.getLength());
        this.maxLengthReached = true;
      } else {
        this.maxLengthReached = false;
      }

      const lineCount = ($event.htmlValue.match(/<\/li>/g) || []).length + ($event.htmlValue.match(/<\/p>/g) || []).length;
      this.maxLinesReached = lineCount > 5;
    }
  }

  formatHtmlContent(htmlContent: any) {
    const substring = '<a href="https://www.';
    return replace(htmlContent, new RegExp('<a href="www.', 'g'), substring);
  }

  saveEditorInstance($event: any) {
    this.editor = $event.editor;
  }
}
